import React, { useState, useEffect, useRef } from "react";
import {
  ArrowRight,
  ArrowLeft,
  ArrowLeftRight,
  Plus,
  Edit,
  Trash2,
  ToggleLeft,
  ToggleRight,
  X,
  Loader2,
  RefreshCw,
  ChevronDown,
  AlertTriangle,
  Search,
  AlertCircle,
} from "lucide-react";
import {
  fieldMappingService,
  FieldMapping as FieldMappingType,
  HubSpotProperty,
  CreateFieldMappingRequest,
  ContractField,
} from "../../services/FieldMappingService";
import { useToastContext } from "../../context/ToastContext";

// Contract fields will be loaded from API

// Revenue fields that should trigger warning when mapping to Company/Contact
const REVENUE_FIELDS = [
  'mrr',
  'monthlyrecurringrevenue',
  'arr',
  'annualrecurringrevenue',
  'tcv',
  'totalcontractvalue',
  'acv',
  'annualcontractvalue',
  'renewablebase',
];

const FieldMapping: React.FC = () => {
  const { showToast } = useToastContext();
  const [mappings, setMappings] = useState<FieldMappingType[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedObjectType, setSelectedObjectType] = useState<
    "deal" | "company" | "contact"
  >("deal");
  const [contractFields, setContractFields] = useState<ContractField[]>([]);
  const [loadingContractFields, setLoadingContractFields] = useState(false);
  const [statusFilter, setStatusFilter] = useState<boolean | "all">("all");
  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
  const statusFilterRef = useRef<HTMLDivElement>(null);
  const [isAddMappingOpen, setIsAddMappingOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isOpening, setIsOpening] = useState(false);
  const [editingMapping, setEditingMapping] = useState<FieldMappingType | null>(
    null
  );
  const [hubspotProperties, setHubspotProperties] = useState<
    HubSpotProperty[]
  >([]);
  const [loadingProperties, setLoadingProperties] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showProductFieldSuggestions, setShowProductFieldSuggestions] =
    useState(false);
  const [deleteConfirmMapping, setDeleteConfirmMapping] = useState<FieldMappingType | null>(null);
  const productFieldInputRef = useRef<HTMLInputElement>(null);
  const productFieldDropdownRef = useRef<HTMLDivElement>(null);
  const [isHubSpotFieldDropdownOpen, setIsHubSpotFieldDropdownOpen] = useState(false);
  const [hubSpotFieldSearch, setHubSpotFieldSearch] = useState("");
  const hubSpotFieldDropdownRef = useRef<HTMLDivElement>(null);

  const [formData, setFormData] = useState<CreateFieldMappingRequest>({
    contractField: "",
    hubspotField: "",
    hubspotObjectType: "deal",
    status: true,
    syncOnCreate: true,
    syncOnUpdate: true,
  });

  // Load contract fields on mount
  useEffect(() => {
    loadContractFields();
  }, []);

  // Load mappings when object type or status filter changes
  useEffect(() => {
    loadMappings();
  }, [selectedObjectType, statusFilter]);

  // Close status filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        statusFilterRef.current &&
        !statusFilterRef.current.contains(event.target as Node)
      ) {
        setIsStatusFilterOpen(false);
      }
      if (
        hubSpotFieldDropdownRef.current &&
        !hubSpotFieldDropdownRef.current.contains(event.target as Node)
      ) {
        setIsHubSpotFieldDropdownOpen(false);
      }
    };

    if (isStatusFilterOpen || isHubSpotFieldDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [isStatusFilterOpen, isHubSpotFieldDropdownOpen]);

  // Load HubSpot properties when modal opens or object type changes
  useEffect(() => {
    if (isAddMappingOpen && !isClosing) {
      loadHubSpotProperties();
    }
  }, [isAddMappingOpen, formData.hubspotObjectType, selectedObjectType]);

  // Close product field suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        productFieldDropdownRef.current &&
        !productFieldDropdownRef.current.contains(event.target as Node) &&
        productFieldInputRef.current &&
        !productFieldInputRef.current.contains(event.target as Node)
      ) {
        setShowProductFieldSuggestions(false);
      }
    };

    if (showProductFieldSuggestions) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [showProductFieldSuggestions]);

  const loadContractFields = async () => {
    setLoadingContractFields(true);
    try {
      const response = await fieldMappingService.getContractFields();
      setContractFields(response.data.contractFields);
    } catch (error: any) {
      console.error("Error loading contract fields:", error);
      showToast(error.message || "Failed to load contract fields", "error");
    } finally {
      setLoadingContractFields(false);
    }
  };

  const loadMappings = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fieldMappingService.getFieldMappings({
        hubspotObjectType: selectedObjectType,
        status: statusFilter === "all" ? undefined : statusFilter,
      });
      setMappings(response.data.mappings);
    } catch (error: any) {
      console.error("Error loading mappings:", error);
      const errorMessage = error.message || "Failed to load field mappings";
      setError(errorMessage);
      showToast(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  const loadHubSpotProperties = async () => {
    setLoadingProperties(true);
    try {
      const response = await fieldMappingService.getHubSpotProperties(
        formData.hubspotObjectType || selectedObjectType
      );
      setHubspotProperties(response.data.properties);
    } catch (error: any) {
      console.error("Error loading HubSpot properties:", error);
      setError(error.message || "Failed to load HubSpot properties");
    } finally {
      setLoadingProperties(false);
    }
  };

  const handleClose = () => {
    if (isClosing) return;
    setIsClosing(true);
    setTimeout(() => {
      setIsAddMappingOpen(false);
      setIsClosing(false);
      setEditingMapping(null);
      setError(null);
      setFormData({
        contractField: "",
        hubspotField: "",
        hubspotObjectType: selectedObjectType,
        status: true,
        syncOnCreate: true,
        syncOnUpdate: true,
      });
      setHubSpotFieldSearch("");
      setIsHubSpotFieldDropdownOpen(false);
    }, 150);
  };

  const handleOpenAdd = () => {
    setIsClosing(false);
    setIsOpening(true);
    setIsAddMappingOpen(true);
    setFormData({
      contractField: "",
      hubspotField: "",
      hubspotObjectType: selectedObjectType,
      status: true,
      syncOnCreate: true,
      syncOnUpdate: true,
    });
    setError(null);
    setTimeout(() => {
      setIsOpening(false);
    }, 10);
  };

  const handleEdit = (mapping: FieldMappingType) => {
    setEditingMapping(mapping);
    setFormData({
      contractField: mapping.contractField,
      hubspotField: mapping.hubspotField,
      hubspotObjectType: mapping.hubspotObjectType,
      status: mapping.status,
      syncOnCreate: mapping.syncOnCreate,
      syncOnUpdate: mapping.syncOnUpdate,
      fieldType: mapping.fieldType,
      transformation: mapping.transformation,
    });
    setSelectedObjectType(mapping.hubspotObjectType);
    setIsClosing(false);
    setIsOpening(true);
    setIsAddMappingOpen(true);
    setTimeout(() => {
      setIsOpening(false);
    }, 10);
  };

  const handleDelete = (mapping: FieldMappingType) => {
    setDeleteConfirmMapping(mapping);
  };

  const confirmDelete = async () => {
    if (!deleteConfirmMapping) return;

    try {
      await fieldMappingService.deleteFieldMapping(deleteConfirmMapping._id);
      showToast("Field mapping deleted successfully", "success");
      await loadMappings();
      setDeleteConfirmMapping(null);
    } catch (error: any) {
      console.error("Error deleting mapping:", error);
      const errorMessage = error.message || "Failed to delete field mapping";
      showToast(errorMessage, "error");
    }
  };

  const handleToggleStatus = async (mapping: FieldMappingType) => {
    const previousStatus = mapping.status;
    // Optimistic update
    setMappings((prev) =>
      prev.map((m) =>
        m._id === mapping._id ? { ...m, status: !m.status } : m
      )
    );

    try {
      await fieldMappingService.updateFieldMapping(mapping._id, {
        status: !mapping.status,
      });
      showToast(
        `Field mapping ${!mapping.status ? "activated" : "deactivated"}`,
        "success"
      );
      await loadMappings();
    } catch (error: any) {
      // Rollback on error
      setMappings((prev) =>
        prev.map((m) =>
          m._id === mapping._id ? { ...m, status: previousStatus } : m
        )
      );
      console.error("Error updating mapping:", error);
      const errorMessage = error.message || "Failed to update field mapping";
      showToast(errorMessage, "error");
    }
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      if (editingMapping) {
        await fieldMappingService.updateFieldMapping(
          editingMapping._id,
          formData
        );
      } else {
        await fieldMappingService.createFieldMapping(formData);
      }
      await loadMappings();
      showToast(
        editingMapping
          ? "Field mapping updated successfully"
          : "Field mapping created successfully",
        "success"
      );
      handleClose();
    } catch (error: any) {
      console.error("Error saving mapping:", error);
      let errorMessage = "Failed to save field mapping";
      if (error.response?.status === 409) {
        errorMessage =
          "A mapping already exists for this contract field, HubSpot field, and object type combination.";
      } else {
        errorMessage =
          error?.response?.data?.message ||
          error.message ||
          "Failed to save field mapping";
      }
      setError(errorMessage);
      showToast(errorMessage, "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleContractFieldSelect = (field: string) => {
    setFormData({ ...formData, contractField: field });
    setShowProductFieldSuggestions(false);
  };

  // Check if we should show the multiple contracts warning
  const shouldShowWarning = (): boolean => {
    if (!formData.contractField || !formData.hubspotObjectType) {
      return false;
    }
    
    const isCompanyOrContact = 
      formData.hubspotObjectType === 'company' || 
      formData.hubspotObjectType === 'contact';
    
    const isRevenueField = REVENUE_FIELDS.includes(
      formData.contractField.toLowerCase()
    );
    
    return isCompanyOrContact && isRevenueField;
  };

  return (
    <div className="w-full min-h-screen bg-background-default dark:bg-background-dark">
      <div className="max-w-7xl mx-auto px-8 py-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-semibold text-text-primary dark:text-text-primaryDark mb-2">
              Field Mapping
            </h1>
            <p className="text-base text-text-secondary dark:text-text-secondaryDark">
              Map RevSync contract fields to HubSpot properties for automatic data synchronization
            </p>
          </div>
          <button
            onClick={handleOpenAdd}
            className="flex items-center gap-2 px-4 py-2.5 bg-black hover:bg-black/90 dark:bg-secondary dark:hover:bg-secondary-hover text-white rounded-lg text-sm font-medium transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Mapping
          </button>
        </div>

        {/* Info Card */}
        <div className="bg-background-paper dark:bg-background-paperDark rounded-lg p-6 border border-divider dark:border-divider-dark mb-8">
          <div className="flex items-center gap-2 mb-3">
            <span className="px-3 py-1 bg-green-light dark:bg-green-dark/20 text-green-dark dark:text-green-dark rounded-full text-xs font-medium">
              RevSync Contracts
            </span>
            <ArrowRight className="w-4 h-4 text-text-secondary dark:text-text-secondaryDark" />
            <span className="px-3 py-1 bg-secondary/10 text-secondary rounded-full text-xs font-medium">
              HubSpot
            </span>
          </div>
          <p className="text-sm text-text-secondary dark:text-text-secondaryDark leading-relaxed">
            Map contract fields (MRR, ARR, TCV, etc.) to HubSpot properties. Data flows one-way from RevSync contracts to HubSpot for sales and marketing visibility.
          </p>
        </div>

        {/* Object Type Filter Tabs */}
        <div className="flex items-center gap-2 mb-6 border-b border-divider dark:border-divider-dark">
          {(
            [
              { value: "deal", label: "Deal ⭐", description: "Most common for RevSync" },
              { value: "company", label: "Company" },
              { value: "contact", label: "Contact" },
            ] as const
          ).map((type) => (
            <button
              key={type.value}
              onClick={() => setSelectedObjectType(type.value)}
              className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
                selectedObjectType === type.value
                  ? "border-secondary text-secondary"
                  : "border-transparent text-text-secondary dark:text-text-secondaryDark hover:text-text-primary dark:hover:text-text-primaryDark"
              }`}
              title={type.description}
            >
              {type.label}
            </button>
          ))}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 mb-6">
          {/* Status Filter */}
          <div className="relative" ref={statusFilterRef}>
            <button
              onClick={() => setIsStatusFilterOpen(!isStatusFilterOpen)}
              className="px-3 py-1.5 text-sm font-medium bg-background-paper dark:bg-background-paperDark border border-divider dark:border-divider-dark rounded-md text-text-primary dark:text-text-primaryDark hover:bg-background-panel dark:hover:bg-background-panelDark hover:border-text-secondary dark:hover:border-text-secondaryDark transition-colors flex items-center gap-2 min-w-[120px] justify-between"
            >
              <span>
                {statusFilter === "all"
                  ? "All Status"
                  : statusFilter
                  ? "Active"
                  : "Inactive"}
              </span>
              <ChevronDown
                className={`w-4 h-4 transition-transform ${
                  isStatusFilterOpen ? "rotate-180" : ""
                }`}
              />
            </button>

            {/* Dropdown menu */}
            {isStatusFilterOpen && (
              <div className="absolute top-full left-0 mt-2 bg-background-paper dark:bg-background-paperDark border border-divider dark:border-divider-dark rounded-md shadow-lg z-50 min-w-[120px] overflow-hidden">
                {[
                  { value: "all", label: "All Status" },
                  { value: "true", label: "Active" },
                  { value: "false", label: "Inactive" },
                ].map((option) => {
                  const isSelected =
                    (option.value === "all" && statusFilter === "all") ||
                    (option.value === "true" && statusFilter === true) ||
                    (option.value === "false" && statusFilter === false);
                  return (
                    <button
                      key={option.value}
                      onClick={() => {
                        setStatusFilter(
                          option.value === "all"
                            ? "all"
                            : option.value === "true"
                        );
                        setIsStatusFilterOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                        isSelected
                          ? "bg-secondary/10 text-secondary dark:bg-secondary/20 dark:text-secondary font-medium"
                          : "text-text-primary dark:text-text-primaryDark hover:bg-background-panel dark:hover:bg-background-panelDark"
                      }`}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        {/* Field Mapping Table */}
        <div className="bg-background-paper dark:bg-background-paperDark rounded-lg border border-divider dark:border-divider-dark overflow-hidden mb-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-secondary" />
              <span className="ml-3 text-sm text-text-secondary dark:text-text-secondaryDark">
                Loading mappings...
              </span>
            </div>
          ) : mappings.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <p className="text-sm text-text-secondary dark:text-text-secondaryDark mb-4">
                No field mappings found for {selectedObjectType}
              </p>
              <button
                onClick={handleOpenAdd}
                className="flex items-center gap-2 px-4 py-2 bg-black hover:bg-black/90 dark:bg-secondary dark:hover:bg-secondary-hover text-white rounded-lg text-sm font-medium transition-colors"
              >
                <Plus className="w-4 h-4" />
                Create First Mapping
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full table-auto border-collapse">
                <thead>
                  <tr className="border-b border-divider dark:border-divider-dark bg-background-panel dark:bg-background-panelDark">
                    <th className="text-left py-3 px-6 text-sm font-semibold text-text-primary dark:text-text-primaryDark">
                      Contract Field
                    </th>
                    <th className="text-left py-3 px-6 text-sm font-semibold text-text-primary dark:text-text-primaryDark">
                      HubSpot Object
                    </th>
                    <th className="text-left py-3 px-6 text-sm font-semibold text-text-primary dark:text-text-primaryDark">
                      HubSpot Field
                    </th>
                    <th className="text-left py-3 px-6 text-sm font-semibold text-text-primary dark:text-text-primaryDark">
                      Status
                    </th>
                    <th className="text-right py-3 px-6 text-sm font-semibold text-text-primary dark:text-text-primaryDark">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {mappings.map((mapping, index) => (
                    <tr
                      key={mapping._id}
                      className={`border-b border-divider dark:border-divider-dark ${
                        index === mappings.length - 1 ? "border-b-0" : ""
                      } hover:bg-background-panel dark:hover:bg-background-panelDark transition-colors`}
                    >
                      <td className="py-4 px-6">
                        <span className="text-sm font-mono text-text-primary dark:text-text-primaryDark bg-background-panel dark:bg-background-panelDark px-2 py-1 rounded">
                          {mapping.contractField}
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        <span className="text-sm text-text-primary dark:text-text-primaryDark capitalize">
                          {mapping.hubspotObjectType}
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        <span className="text-sm font-mono text-text-primary dark:text-text-primaryDark bg-background-panel dark:bg-background-panelDark px-2 py-1 rounded">
                          {mapping.hubspotField}
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        <button
                          onClick={() => handleToggleStatus(mapping)}
                          className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                        >
                          <div
                            className={`w-2 h-2 rounded-full ${
                              mapping.status
                                ? "bg-green-dark"
                                : "bg-text-secondary dark:text-text-secondaryDark"
                            }`}
                          />
                          <span
                            className={`text-sm ${
                              mapping.status
                                ? "text-text-primary dark:text-text-primaryDark"
                                : "text-text-secondary dark:text-text-secondaryDark"
                            }`}
                          >
                            {mapping.status ? "Active" : "Inactive"}
                          </span>
                        </button>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center justify-end gap-3">
                          <button
                            onClick={() => handleEdit(mapping)}
                            className="p-1.5 hover:bg-background-panel dark:hover:bg-background-panelDark rounded transition-colors"
                          >
                            <Edit className="w-4 h-4 text-text-secondary dark:text-text-secondaryDark hover:text-text-primary dark:hover:text-text-primaryDark" />
                          </button>
                          <button
                            onClick={() => handleDelete(mapping)}
                            className="p-1.5 hover:bg-background-panel dark:hover:bg-background-panelDark rounded transition-colors"
                          >
                            <Trash2 className="w-4 h-4 text-text-secondary dark:text-text-secondaryDark hover:text-red-dark" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Automatic Sync Configuration Info Card */}
        <div className="bg-background-paper dark:bg-background-paperDark rounded-lg p-6 border border-divider dark:border-divider-dark">
          <h3 className="text-lg font-semibold text-text-primary dark:text-text-primaryDark mb-2">
            Automatic Sync Configuration
          </h3>
          <p className="text-sm text-text-secondary dark:text-text-secondaryDark leading-relaxed">
            Field mappings sync automatically when contracts are created or updated. Data flows one-way from RevSync contracts to HubSpot.
          </p>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirmMapping && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-background-paper dark:bg-background-paperDark rounded-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-6 border-b border-divider dark:border-divider-dark">
              <h2 className="text-xl font-semibold text-text-primary dark:text-text-primaryDark">
                Delete Field Mapping
              </h2>
              <button
                onClick={() => setDeleteConfirmMapping(null)}
                className="p-2 hover:bg-background-panel dark:hover:bg-background-panelDark rounded-lg text-text-secondary hover:text-text-primary transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-red-100 dark:bg-red-900/20 rounded-full">
                  <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <h3 className="font-medium text-text-primary dark:text-text-primaryDark mb-2">
                    Delete mapping between "{deleteConfirmMapping.contractField}" and "{deleteConfirmMapping.hubspotField}"?
                  </h3>
                  <p className="text-text-secondary dark:text-text-secondaryDark text-sm">
                    This will stop syncing this contract field to HubSpot. This action cannot be undone.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 px-6 py-4 border-t border-divider dark:border-divider-dark">
              <button
                onClick={() => setDeleteConfirmMapping(null)}
                className="px-4 py-2 text-text-secondary dark:text-text-secondaryDark hover:text-text-primary dark:hover:text-text-primaryDark transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
              >
                Delete Mapping
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Mapping Slide-Over Panel */}
      {isAddMappingOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-md z-50"
            style={{
              opacity: isClosing ? 0 : isOpening ? 0 : 1,
              pointerEvents: isClosing ? "none" : "auto",
              transition: isClosing
                ? "opacity 0.2s cubic-bezier(0.4, 0, 1, 1)"
                : "opacity 0.3s cubic-bezier(0, 0, 0.2, 1)",
            }}
            onClick={handleClose}
          />

          {/* Slide-Over Panel */}
          <div
            className="fixed inset-y-0 right-0 w-full max-w-2xl bg-background-paper dark:bg-background-paperDark shadow-2xl z-50 overflow-y-auto"
            style={{
              transform: isOpening
                ? "translateX(100%)"
                : isClosing
                ? "translateX(100%)"
                : "translateX(0)",
              opacity: isClosing ? 0 : 1,
              transition: isClosing
                ? "transform 0.2s cubic-bezier(0.4, 0, 1, 1), opacity 0.2s cubic-bezier(0.4, 0, 1, 1)"
                : "transform 0.3s cubic-bezier(0, 0, 0.2, 1), opacity 0.3s cubic-bezier(0, 0, 0.2, 1)",
            }}
          >
            <form onSubmit={handleSubmit} className="flex flex-col h-full">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-divider dark:border-divider-dark">
                <div>
                  <h2 className="text-xl font-semibold text-text-primary dark:text-text-primaryDark">
                    {editingMapping
                      ? "Edit Field Mapping"
                      : "Add Field Mapping"}
                  </h2>
                  <p className="text-sm text-text-secondary dark:text-text-secondaryDark mt-1">
                    {editingMapping
                      ? "Update the mapping between RevSync contract fields and HubSpot properties"
                      : "Create a new mapping from RevSync contract fields to HubSpot properties"}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleClose}
                  className="p-2 hover:bg-background-panel dark:hover:bg-background-panelDark rounded-lg transition-colors"
                  aria-label="Close"
                >
                  <X className="w-5 h-5 text-text-secondary dark:text-text-secondaryDark" />
                </button>
              </div>

              {/* Form Content */}
              <div className="flex-1 px-6 py-6">
                <div className="space-y-6">
                  {/* HubSpot Object Type */}
                  <div>
                    <label className="block text-sm font-medium text-text-primary dark:text-text-primaryDark mb-2">
                      HubSpot Object Type
                    </label>
                    <select
                      value={formData.hubspotObjectType || selectedObjectType}
                      onChange={(e) => {
                        const newObjectType = e.target.value as "deal" | "company" | "contact";
                        setFormData({
                          ...formData,
                          hubspotObjectType: newObjectType,
                          hubspotField: "", // Reset HubSpot field when object type changes
                        });
                        setSelectedObjectType(newObjectType);
                      }}
                      disabled={!!editingMapping}
                      className="w-full px-4 py-2.5 bg-background-default dark:bg-background-dark border border-divider dark:border-divider-dark rounded-lg text-text-primary dark:text-text-primaryDark text-sm focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary transition-colors"
                    >
                      <option value="deal">Deal</option>
                      <option value="company">Company</option>
                      <option value="contact">Contact</option>
                    </select>
                    {editingMapping && (
                      <p className="text-xs text-text-secondary dark:text-text-secondaryDark mt-1.5">
                        Object type cannot be changed when editing
                      </p>
                    )}
                  </div>

                  {/* HubSpot Field */}
                  <div>
                    <label className="block text-sm font-medium text-text-primary dark:text-text-primaryDark mb-2">
                      HubSpot Field
                    </label>
                    {loadingProperties ? (
                      <div className="flex items-center gap-2 px-4 py-2.5 bg-background-default dark:bg-background-dark border border-divider dark:border-divider-dark rounded-lg">
                        <Loader2 className="w-4 h-4 animate-spin text-secondary" />
                        <span className="text-sm text-text-secondary dark:text-text-secondaryDark">
                          Loading properties...
                        </span>
                      </div>
                    ) : (
                      <div className="relative" ref={hubSpotFieldDropdownRef}>
                        <button
                          type="button"
                          onClick={() => {
                            setIsHubSpotFieldDropdownOpen(!isHubSpotFieldDropdownOpen);
                            if (!isHubSpotFieldDropdownOpen) {
                              setHubSpotFieldSearch("");
                            }
                          }}
                          className="w-full flex items-center justify-between px-4 py-2.5 bg-background-default dark:bg-background-dark border border-divider dark:border-divider-dark rounded-lg text-sm text-text-primary dark:text-text-primaryDark hover:border-secondary/50 transition-colors text-left"
                        >
                          <span className={formData.hubspotField ? "" : "text-text-secondary dark:text-text-secondaryDark"}>
                            {formData.hubspotField
                              ? (() => {
                                  const prop = hubspotProperties.find((p) => p.name === formData.hubspotField);
                                  return prop ? `${prop.label} (${prop.name})` : formData.hubspotField;
                                })()
                              : "Select HubSpot field..."}
                          </span>
                          <ChevronDown
                            className={`w-4 h-4 text-text-secondary dark:text-text-secondaryDark transition-transform flex-shrink-0 ${
                              isHubSpotFieldDropdownOpen ? "rotate-180" : ""
                            }`}
                          />
                        </button>
                        {isHubSpotFieldDropdownOpen && (
                          <div className="absolute z-20 w-full mt-1 bg-background-paper dark:bg-background-paperDark border border-divider dark:border-divider-dark rounded-lg shadow-lg overflow-hidden">
                            {/* Search Input */}
                            <div className="p-2 border-b border-divider dark:border-divider-dark">
                              <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary dark:text-text-secondaryDark" />
                                <input
                                  type="text"
                                  value={hubSpotFieldSearch}
                                  onChange={(e) => setHubSpotFieldSearch(e.target.value)}
                                  placeholder="Search fields..."
                                  className="w-full pl-9 pr-3 py-2 bg-background-default dark:bg-background-dark border border-divider dark:border-divider-dark rounded-lg text-sm text-text-primary dark:text-text-primaryDark focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary"
                                  autoFocus
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </div>
                            </div>
                            {/* Filtered List */}
                            <div className="max-h-64 overflow-y-auto">
                              {(() => {
                                const filtered = hubspotProperties.filter((prop) => {
                                  const searchLower = hubSpotFieldSearch.toLowerCase();
                                  return (
                                    prop.label.toLowerCase().includes(searchLower) ||
                                    prop.name.toLowerCase().includes(searchLower)
                                  );
                                });

                                if (filtered.length === 0) {
                                  return (
                                    <div className="px-4 py-8 text-center text-sm text-text-secondary dark:text-text-secondaryDark">
                                      No fields found matching "{hubSpotFieldSearch}"
                                    </div>
                                  );
                                }

                                return filtered.map((prop) => (
                                  <button
                                    key={prop.name}
                                    type="button"
                                    onClick={() => {
                                      setFormData({
                                        ...formData,
                                        hubspotField: prop.name,
                                      });
                                      setIsHubSpotFieldDropdownOpen(false);
                                      setHubSpotFieldSearch("");
                                    }}
                                    className={`w-full text-left px-4 py-2.5 text-sm hover:bg-background-panel dark:hover:bg-background-panelDark transition-colors ${
                                      formData.hubspotField === prop.name
                                        ? "bg-background-panel dark:bg-background-panelDark text-secondary"
                                        : "text-text-primary dark:text-text-primaryDark"
                                    }`}
                                  >
                                    <div className="font-medium">{prop.label}</div>
                                    <div className="text-xs text-text-secondary dark:text-text-secondaryDark mt-0.5">
                                      {prop.name}
                                    </div>
                                  </button>
                                ));
                              })()}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    <p className="text-xs text-text-secondary dark:text-text-secondaryDark mt-1.5">
                      Select the field from HubSpot
                    </p>
                  </div>

                  {/* Contract Field */}
                  <div>
                    <label className="block text-sm font-medium text-text-primary dark:text-text-primaryDark mb-2">
                      Contract Field
                    </label>
                    {loadingContractFields ? (
                      <div className="flex items-center gap-2 px-4 py-2.5 bg-background-default dark:bg-background-dark border border-divider dark:border-divider-dark rounded-lg">
                        <Loader2 className="w-4 h-4 animate-spin text-secondary" />
                        <span className="text-sm text-text-secondary dark:text-text-secondaryDark">
                          Loading contract fields...
                        </span>
                      </div>
                    ) : (
                      <div className="relative" ref={productFieldDropdownRef}>
                        <input
                          ref={productFieldInputRef}
                          type="text"
                          value={formData.contractField}
                          onChange={(e) => {
                            setFormData({
                              ...formData,
                              contractField: e.target.value,
                            });
                            setShowProductFieldSuggestions(true);
                          }}
                          onFocus={() => setShowProductFieldSuggestions(true)}
                          placeholder="e.g., mrr, arr, contractName"
                          required
                          className="w-full px-4 py-2.5 bg-background-default dark:bg-background-dark border border-divider dark:border-divider-dark rounded-lg text-text-primary dark:text-text-primaryDark placeholder:text-text-secondary dark:placeholder:text-text-secondaryDark focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary transition-colors font-mono text-sm"
                        />
                        {showProductFieldSuggestions &&
                          contractFields
                            .filter((field) =>
                              field.name
                                .toLowerCase()
                                .includes(
                                  formData.contractField.toLowerCase()
                                ) ||
                              field.label
                                .toLowerCase()
                                .includes(
                                  formData.contractField.toLowerCase()
                                )
                            )
                            .length > 0 && (
                            <div className="absolute z-10 w-full mt-1 bg-background-paper dark:bg-background-paperDark border border-divider dark:border-divider-dark rounded-lg shadow-lg max-h-60 overflow-auto">
                              {contractFields
                                .filter((field) =>
                                  field.name
                                    .toLowerCase()
                                    .includes(
                                      formData.contractField.toLowerCase()
                                    ) ||
                                  field.label
                                    .toLowerCase()
                                    .includes(
                                      formData.contractField.toLowerCase()
                                    )
                                )
                                .map((field) => (
                                  <button
                                    key={field.name}
                                    type="button"
                                    onClick={() => handleContractFieldSelect(field.name)}
                                    className="w-full text-left px-4 py-2.5 text-sm hover:bg-background-panel dark:hover:bg-background-panelDark transition-colors text-text-primary dark:text-text-primaryDark"
                                  >
                                    <div className="font-medium">{field.label}</div>
                                    <div className="text-xs text-text-secondary dark:text-text-secondaryDark font-mono">
                                      {field.name}
                                    </div>
                                    {field.description && (
                                      <div className="text-xs text-text-secondary dark:text-text-secondaryDark mt-0.5">
                                        {field.description}
                                      </div>
                                    )}
                                  </button>
                                ))}
                            </div>
                          )}
                      </div>
                    )}
                    <p className="text-xs text-text-secondary dark:text-text-secondaryDark mt-1.5">
                      Select a contract field to map to HubSpot
                    </p>
                  </div>

                  {/* Multiple Contracts Warning */}
                  {shouldShowWarning() && (
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800/30 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <h4 className="text-sm font-semibold text-yellow-800 dark:text-yellow-200 mb-2">
                            Multiple Contracts Warning
                          </h4>
                          <p className="text-sm text-yellow-700 dark:text-yellow-300 mb-2">
                            If this customer has multiple contracts, only the most recently synced contract's value will be shown in this {formData.hubspotObjectType === 'company' ? 'Company' : 'Contact'} field.
                          </p>
                          <p className="text-sm text-yellow-700 dark:text-yellow-300">
                            For accurate revenue tracking per contract, we recommend mapping to the <strong className="font-semibold">Deal</strong> object instead.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Status */}
                  <div>
                    <label className="block text-sm font-medium text-text-primary dark:text-text-primaryDark mb-3">
                      Status
                    </label>
                    <button
                      type="button"
                      onClick={() =>
                        setFormData({
                          ...formData,
                          status: !formData.status,
                        })
                      }
                      className="flex items-center gap-3 p-4 border border-divider dark:border-divider-dark rounded-lg hover:border-secondary/50 dark:hover:border-secondary/50 transition-colors bg-background-default dark:bg-background-dark w-full"
                    >
                      {formData.status ? (
                        <ToggleRight className="w-5 h-5 text-secondary" />
                      ) : (
                        <ToggleLeft className="w-5 h-5 text-text-secondary dark:text-text-secondaryDark" />
                      )}
                      <div className="text-left">
                        <span
                          className={`text-sm font-medium ${
                            formData.status
                              ? "text-secondary"
                              : "text-text-secondary dark:text-text-secondaryDark"
                          }`}
                        >
                          {formData.status ? "Active" : "Inactive"}
                        </span>
                        <p className="text-xs text-text-secondary dark:text-text-secondaryDark mt-0.5">
                          {formData.status
                            ? "Mapping will sync automatically"
                            : "Mapping is disabled"}
                        </p>
                      </div>
                    </button>
                  </div>

                  {/* Sync Options */}
                  <div className="space-y-3">
                    <label className="block text-sm font-medium text-text-primary dark:text-text-primaryDark">
                      Sync Options
                    </label>
                    <div className="space-y-2">
                      <label className="flex items-center gap-3 p-3 border border-divider dark:border-divider-dark rounded-lg hover:border-secondary/50 dark:hover:border-secondary/50 transition-colors bg-background-default dark:bg-background-dark cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.syncOnCreate}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              syncOnCreate: e.target.checked,
                            })
                          }
                          className="w-4 h-4 rounded border-divider dark:border-divider-dark text-secondary focus:ring-secondary focus:ring-offset-0"
                        />
                        <div>
                          <span className="text-sm font-medium text-text-primary dark:text-text-primaryDark">
                            Sync On Create
                          </span>
                          <p className="text-xs text-text-secondary dark:text-text-secondaryDark mt-0.5">
                            Automatically sync when new records are created
                          </p>
                        </div>
                      </label>
                      <label className="flex items-center gap-3 p-3 border border-divider dark:border-divider-dark rounded-lg hover:border-secondary/50 dark:hover:border-secondary/50 transition-colors bg-background-default dark:bg-background-dark cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.syncOnUpdate}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              syncOnUpdate: e.target.checked,
                            })
                          }
                          className="w-4 h-4 rounded border-divider dark:border-divider-dark text-secondary focus:ring-secondary focus:ring-offset-0"
                        />
                        <div>
                          <span className="text-sm font-medium text-text-primary dark:text-text-primaryDark">
                            Sync On Update
                          </span>
                          <p className="text-xs text-text-secondary dark:text-text-secondaryDark mt-0.5">
                            Automatically sync when records are updated
                          </p>
                        </div>
                      </label>
                    </div>
                  </div>


                  {/* Advanced Options (Optional) */}
                  <div className="space-y-4 pt-4 border-t border-divider dark:border-divider-dark">
                    <h3 className="text-sm font-medium text-text-primary dark:text-text-primaryDark">
                      Advanced Options (Optional)
                    </h3>

                    {/* Field Type */}
                    <div>
                      <label className="block text-sm font-medium text-text-primary dark:text-text-primaryDark mb-2">
                        Field Type
                      </label>
                      <div className="relative">
                        <select
                          value={formData.fieldType || ""}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              fieldType: e.target.value || undefined,
                            })
                          }
                          className="w-full px-4 py-2.5 bg-background-default dark:bg-background-dark border border-divider dark:border-divider-dark rounded-lg text-text-primary dark:text-text-primaryDark focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary transition-colors text-sm appearance-none"
                        >
                          <option value="">Auto-detect (recommended)</option>
                          <option value="string">String</option>
                          <option value="number">Number</option>
                          <option value="date">Date</option>
                          <option value="boolean">Boolean</option>
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary dark:text-text-secondaryDark pointer-events-none" />
                      </div>
                      <p className="text-xs text-text-secondary dark:text-text-secondaryDark mt-1.5">
                        Override auto-detected field type if needed
                      </p>
                    </div>

                    {/* Transformation */}
                    <div>
                      <label className="block text-sm font-medium text-text-primary dark:text-text-primaryDark mb-2">
                        Transformation (Advanced)
                      </label>
                      <input
                        type="text"
                        value={formData.transformation || ""}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            transformation: e.target.value || undefined,
                          })
                        }
                        placeholder="e.g., value * 100, value.toUpperCase()"
                        className="w-full px-4 py-2.5 bg-background-default dark:bg-background-dark border border-divider dark:border-divider-dark rounded-lg text-text-primary dark:text-text-primaryDark placeholder:text-text-secondary dark:placeholder:text-text-secondaryDark focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary transition-colors font-mono text-sm"
                      />
                      <p className="text-xs text-text-secondary dark:text-text-secondaryDark mt-1.5">
                        Optional transformation logic to apply to the field value
                      </p>
                    </div>
                  </div>

                  {/* Error Message */}
                  {error && (
                    <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                      <p className="text-sm text-red-600 dark:text-red-400">
                        {error}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Footer Actions */}
              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-divider dark:border-divider-dark bg-background-panel dark:bg-background-panelDark">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 text-sm font-medium text-text-secondary dark:text-text-secondaryDark hover:text-text-primary dark:hover:text-text-primaryDark transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={
                    submitting ||
                    !formData.hubspotField ||
                    !formData.contractField ||
                    !formData.hubspotObjectType
                  }
                  className="px-4 py-2 bg-black hover:bg-black/90 dark:bg-secondary dark:hover:bg-secondary-hover text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {submitting && (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  )}
                  {editingMapping ? "Save Changes" : "Create Mapping"}
                </button>
              </div>
            </form>
          </div>
        </>
      )}
    </div>
  );
};

export default FieldMapping;
